import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftRight, ArrowRight, ArrowUpRight, BadgeCheck, Bot, Brain, Briefcase, Building2, CalendarDays, Captions, ChartColumn,
  Check, ChevronDown, Circle, Clock, Gauge, Globe, Hand, Headphones, Hotel, House, KeyRound, Languages, Laptop, Lock, MapPin,
  Maximize2, Megaphone, MessageSquare, Mic, MonitorUp, Phone, PhoneForwarded, Plug, RefreshCw, Repeat, Route, Search, Server,
  ShieldCheck, ShoppingBag, Smartphone, Stethoscope, Users, Video, Wallet, X, Zap,
} from 'lucide-react';
import Reveal from '../Reveal';
import { useCountries } from '../../hooks/useCountries';
import NumberCountryTable, { ResultsLine, matchesCountry } from '../numbers/NumberCountryTable';
import { useLeadGuard } from '../LeadGuard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ICONS = {
  ArrowLeftRight, BadgeCheck, Bot, Brain, Briefcase, Building2, CalendarDays, Captions, ChartColumn, Clock, Gauge, Globe, Hand,
  Headphones, Hotel, House, KeyRound, Languages, Laptop, Lock, MapPin, Megaphone, MessageSquare, Mic, MonitorUp, Phone,
  PhoneForwarded, Plug, RefreshCw, Repeat, Route, Server, ShieldCheck, ShoppingBag, Smartphone, Stethoscope, Users, Video, Wallet, Zap,
};

export const Icon = ({ name, className }) => {
  const C = ICONS[name] || Circle;
  return <C className={className} strokeWidth={1.75} />;
};

export const SectionTitle = ({ title, sub, center = false, light = false }) => (
  <Reveal className={`mb-12 max-w-2xl ${center ? 'mx-auto text-center' : ''}`}>
    <h2 className={`font-display text-4xl font-semibold tracking-tight md:text-5xl ${light ? 'text-white' : ''}`}>{title}</h2>
    {sub && <p className={`mt-4 text-lg leading-relaxed ${light ? 'text-gray-300' : 'text-gray-600'}`}>{sub}</p>}
  </Reveal>
);

export const CheckItem = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#c8ff00]">
      <Check className="h-3.5 w-3.5 text-[#0a1f2e]" strokeWidth={3} />
    </span>
    <span>{children}</span>
  </li>
);

/** Product hero: headline with a lime-highlighted phrase, bullets, two CTAs and a visual on the right. */
export const ProductHero = ({ title, highlight, after = '', sub, bullets = [], media, primary = { label: 'Get a quote', href: '#quote' }, secondary }) => (
  <section className="overflow-hidden px-4 pb-16 pt-28 sm:px-6 lg:px-8 lg:pt-32">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <Reveal as="h1" className="font-display text-5xl font-semibold leading-[1.08] tracking-tight md:text-6xl">
          {title} {highlight && <span className="whitespace-nowrap rounded-lg bg-[#c8ff00] px-2">{highlight}</span>}{after}
        </Reveal>
        <Reveal as="p" delay={120} className="mt-6 max-w-[50ch] text-lg leading-relaxed text-gray-600">{sub}</Reveal>
        {bullets.length > 0 && (
          <Reveal delay={200} as="ul" className="mt-6 grid max-w-md grid-cols-2 gap-x-6 gap-y-2 text-sm font-medium text-gray-700">
            {bullets.map((b) => <CheckItem key={b}>{b}</CheckItem>)}
          </Reveal>
        )}
        <Reveal delay={280} className="mt-10 flex flex-col gap-4 sm:flex-row">
          <a href={primary.href} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c8ff00] px-8 py-4 font-semibold text-[#0a1f2e] transition hover:brightness-95 active:scale-[0.98]">
            {primary.label} <ArrowRight className="h-5 w-5" />
          </a>
          {secondary && (
            <a href={secondary.href} className="inline-flex items-center justify-center rounded-full border border-[#0a1f2e]/25 px-8 py-4 font-semibold transition-colors hover:border-[#0a1f2e] hover:bg-[#0a1f2e] hover:text-white active:scale-[0.98]">
              {secondary.label}
            </a>
          )}
        </Reveal>
      </div>
      <Reveal delay={150} className="relative lg:col-span-6">{media}</Reveal>
    </div>
  </section>
);

/** Large crystal product icon on a soft lime glow, for hero media without photos. */
export const CrystalPanel = ({ src, className = 'aspect-[4/3]' }) => (
  <div
    className={`flex items-center justify-center rounded-3xl border border-gray-200 ${className}`}
    style={{ background: 'radial-gradient(ellipse at 50% 58%, rgba(200,255,0,0.25), transparent 60%), linear-gradient(to bottom, #f1f6e6, #ffffff)' }}
  >
    <img src={src} alt="" className="h-3/4 w-3/4 object-contain drop-shadow-[0_30px_40px_rgba(10,31,46,0.25)]" />
  </div>
);

export const TrustStrip = ({ items }) => (
  <section className="border-y border-gray-100 px-4 py-10 sm:px-6 lg:px-8">
    <div className={`mx-auto grid max-w-7xl grid-cols-2 gap-8 ${items.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
      {items.map((t) => (
        <div key={t.label}>
          <div className="font-display text-4xl font-semibold">{t.value}</div>
          <div className="mt-1 text-sm text-gray-600">{t.label}</div>
        </div>
      ))}
    </div>
  </section>
);

export const Intro = ({ title, paragraphs, bullets = [] }) => (
  <section className="px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">{title}</h2>
      </Reveal>
      <Reveal delay={120} className="space-y-5 text-lg leading-relaxed text-gray-600 lg:col-span-6">
        {paragraphs.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
        {bullets.length > 0 && (
          <ul className="grid grid-cols-1 gap-3 pt-2 text-base text-gray-700 sm:grid-cols-2">
            {bullets.map((b) => <CheckItem key={b}>{b}</CheckItem>)}
          </ul>
        )}
      </Reveal>
    </div>
  </section>
);

const Lightbox = ({ screen, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={screen.title} className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0a1f2e]/85 p-4 backdrop-blur-sm" onClick={onClose}>
      <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">
        <X className="h-5 w-5" />
      </button>
      <figure className="max-h-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <img src={screen.img} alt={screen.title} className="max-h-[80vh] w-auto rounded-2xl shadow-2xl" />
        <figcaption className="mt-4 text-center text-white">
          <span className="font-semibold">{screen.title}.</span> <span className="text-gray-300">{screen.body}</span>
        </figcaption>
      </figure>
    </div>
  );
};

/** Clickable list of portal screens; the selected screenshot shows large and opens in a lightbox. */
export const ScreenTour = ({ screens }) => {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(null);
  const current = screens[active];
  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
      <div role="tablist" aria-label="Portal screens" className="flex gap-2 overflow-x-auto pb-2 lg:col-span-4 lg:flex-col lg:overflow-visible lg:pb-0">
        {screens.map((s, i) => (
          <button
            key={s.title}
            type="button"
            role="tab"
            aria-selected={active === i}
            onClick={() => setActive(i)}
            className={`min-w-[220px] rounded-2xl border p-5 text-left transition-colors lg:min-w-0 ${active === i ? 'border-[#0a1f2e] bg-[#0a1f2e] text-white' : 'border-gray-200 bg-white hover:border-[#0a1f2e]/40'}`}
          >
            <span className="block font-display text-lg font-semibold">{s.title}</span>
            <span className={`mt-1 block text-sm leading-relaxed ${active === i ? 'text-gray-300' : 'text-gray-600'}`}>{s.body}</span>
          </button>
        ))}
      </div>
      <div className="lg:col-span-8">
        <button
          type="button"
          onClick={() => setZoom(current)}
          className="group relative block w-full overflow-hidden rounded-3xl border border-gray-200 bg-white p-2 shadow-[0_30px_80px_-30px_rgba(10,31,46,0.35)]"
          aria-label={`Enlarge: ${current.title}`}
        >
          <img key={current.img} src={current.img} alt={`VOIP2 portal: ${current.title}`} className="aspect-video w-full rounded-2xl object-cover object-top" />
          <span className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-[#0a1f2e] px-4 py-2 text-sm font-semibold text-white opacity-90 transition group-hover:opacity-100">
            <Maximize2 className="h-4 w-4" /> Enlarge
          </span>
        </button>
        {/* Preload the other screens so switching tabs is instant. */}
        <div className="hidden">{screens.map((s) => <img key={s.img} src={s.img} alt="" />)}</div>
      </div>
      {zoom && <Lightbox screen={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
};

/** Single screenshot in a framed card that opens in the lightbox. */
export const ScreenShot = ({ screen }) => {
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setZoom(true)}
        aria-label={`Enlarge: ${screen.title}`}
        className="group relative block w-full overflow-hidden rounded-3xl border border-gray-200 bg-white p-2 shadow-[0_30px_80px_-30px_rgba(10,31,46,0.35)]"
      >
        <img src={screen.img} alt={`VOIP2 portal: ${screen.title}`} loading="lazy" className="aspect-video w-full rounded-2xl object-cover object-top" />
        <span className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-[#0a1f2e] px-4 py-2 text-sm font-semibold text-white opacity-90 transition group-hover:opacity-100">
          <Maximize2 className="h-4 w-4" /> Enlarge
        </span>
      </button>
      {zoom && <Lightbox screen={screen} onClose={() => setZoom(false)} />}
    </>
  );
};

export const IconCards = ({ items, columns = 3, dark = false }) => (
  <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${columns === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
    {items.map((c, i) => (
      <Reveal key={c.title} delay={(i % columns) * 70} className={`rounded-2xl p-6 ${dark ? 'border border-white/10 bg-white/5' : 'border border-gray-200 bg-white'}`}>
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${dark ? 'bg-[#c8ff00] text-[#0a1f2e]' : 'bg-[#0a1f2e] text-[#c8ff00]'}`}><Icon name={c.icon} className="h-5 w-5" /></span>
        <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
        <p className={`mt-1 text-sm leading-relaxed ${dark ? 'text-gray-300' : 'text-gray-600'}`}>{c.body}</p>
      </Reveal>
    ))}
  </div>
);

export const Steps = ({ steps }) => (
  <div className={`grid grid-cols-1 gap-8 ${steps.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
    {steps.map((s, i) => (
      <Reveal key={s.verb} delay={i * 90} className="border-t-2 border-[#c8ff00] pt-5">
        <div className="font-display text-2xl font-semibold">{s.verb}</div>
        <p className="mt-2 text-gray-600">{s.body}</p>
      </Reveal>
    ))}
  </div>
);

/** Comparison table; the last column is VOIP2's and is highlighted. */
export const CompareTable = ({ head, rows }) => (
  <Reveal className="overflow-x-auto rounded-3xl border border-gray-200 bg-white">
    <table className="w-full min-w-[560px] text-left">
      <thead>
        <tr className="border-b border-gray-200">
          {head.map((h, i) => (
            <th key={h || i} className={`px-6 py-4 text-sm font-semibold ${i === head.length - 1 ? 'bg-[#0a1f2e] text-[#c8ff00]' : 'text-gray-500'}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r[0]}>
            {r.map((cell, i) => (
              <td key={i} className={`px-6 py-4 ${i === 0 ? 'font-semibold' : i === r.length - 1 ? 'bg-[#0a1f2e]/[0.03] font-semibold text-[#0a1f2e]' : 'text-gray-600'}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </Reveal>
);

export const Faq = ({ faqs, title = 'Questions we get asked' }) => (
  <section className="px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-3xl">
      <SectionTitle title={title} center />
      <div className="divide-y divide-gray-200 border-y border-gray-200">
        {faqs.map((f) => (
          <details key={f.q} className="group py-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6 font-display text-lg font-semibold">
              {f.q}
              <ChevronDown className="h-5 w-5 shrink-0 text-gray-500 transition-transform group-open:rotate-180" />
            </summary>
            <p className="mt-3 leading-relaxed text-gray-600">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/** Searchable list of countries from the live coverage API, filtered for one number type. */
export const CoverageList = ({ filter, group, onEnquire, noun = 'countries' }) => {
  const { countries, loading, error } = useCountries();
  const [q, setQ] = useState('');
  const available = useMemo(() => countries.filter(filter), [countries, filter]);
  const shown = useMemo(() => available.filter((c) => matchesCountry(c, q)), [available, q]);

  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-4 sm:p-6 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <label className="relative block md:w-96">
          <span className="sr-only">Search countries</span>
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search country or prefix, e.g. +49"
            className="w-full rounded-full border border-gray-300 py-3 pl-12 pr-4 placeholder:text-gray-400 focus:border-[#0a1f2e] focus:outline-none focus:ring-2 focus:ring-[#c8ff00]"
          />
        </label>
        <ResultsLine count={shown.length} total={available.length} loading={loading} noun={noun} />
      </div>
      {error && !loading && <p className="mt-6 text-gray-600">Coverage could not be loaded right now. <Link to="/contact" className="font-semibold underline">Ask our team</Link> for the latest list.</p>}
      {loading ? (
        <div className="mt-6 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-gray-100" />)}
        </div>
      ) : (
        <div className="mt-6">
          <NumberCountryTable countries={shown} group={group} onEnquire={onEnquire} pageSize={12} />
          {!error && shown.length === 0 && (
            <p className="py-10 text-center text-gray-600">No countries match “{q}”. <Link to="/contact" className="font-semibold underline">Ask us</Link>, as new destinations are added regularly.</p>
          )}
        </div>
      )}
    </div>
  );
};

/** Call transcript rendered as a chat, caller on the left and the AI on the right. */
export const ChatTranscript = ({ lines, aiName = 'VOIP2 AI' }) => (
  <ol className="space-y-3">
    {lines.map((l, i) => {
      const ai = l.who === 'ai';
      return (
        <Reveal as="li" key={i} delay={i * 60} className={`flex ${ai ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${ai ? 'rounded-br-md bg-[#0a1f2e] text-white' : 'rounded-bl-md border border-gray-200 bg-white'}`}>
            <span className={`block text-xs font-semibold ${ai ? 'text-[#c8ff00]' : 'text-gray-500'}`}>{ai ? aiName : 'Caller'}</span>
            <span className="mt-0.5 block leading-relaxed">{l.text}</span>
          </div>
        </Reveal>
      );
    })}
  </ol>
);

const QuoteForm = ({ service, contextLabel, contextOptions = [] }) => {
  const empty = { name: '', email: '', phone: '', company: '', context: '', message: '' };
  const [form, setForm] = useState(empty);
  const [state, setState] = useState({ status: 'idle', error: null });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const { guard, guardFields, resetGuard } = useLeadGuard();
  const submit = async (e) => {
    e.preventDefault();
    setState({ status: 'sending', error: null });
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...guardFields(),
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          company: form.company || null,
          service_interest: service,
          message: [form.context && `${contextLabel} ${form.context}`, form.message].filter(Boolean).join('\n') || null,
          source: 'website',
        }),
      });
      resetGuard();
      if (!res.ok) throw new Error();
      setForm(empty);
      setState({ status: 'sent', error: null });
    } catch {
      setState({ status: 'idle', error: 'Something went wrong sending your request. Please try again, or email hello@voip2.co.uk.' });
    }
  };

  if (state.status === 'sent') {
    return (
      <div className="rounded-3xl bg-white p-10 text-center text-[#0a1f2e]">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#c8ff00]"><Check className="h-7 w-7" strokeWidth={3} /></span>
        <h3 className="mt-5 font-display text-2xl font-semibold">Thanks, we have your request</h3>
        <p className="mt-2 text-gray-600">Our team will contact you within one working day.</p>
        <button type="button" onClick={() => setState({ status: 'idle', error: null })} className="mt-6 font-semibold underline decoration-[#c8ff00] decoration-2 underline-offset-4">Send another request</button>
      </div>
    );
  }

  const field = 'mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-[#0a1f2e] placeholder:text-gray-400 focus:border-[#0a1f2e] focus:outline-none focus:ring-2 focus:ring-[#c8ff00]';
  const label = 'block text-sm font-semibold text-[#0a1f2e]';
  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-5 rounded-3xl bg-white p-8 md:grid-cols-2 md:p-10">
      <label className={label}>Name *<input required value={form.name} onChange={set('name')} autoComplete="name" className={field} /></label>
      <label className={label}>Work email *<input required type="email" value={form.email} onChange={set('email')} autoComplete="email" className={field} /></label>
      <label className={label}>Phone<input type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" className={field} /></label>
      <label className={label}>Company<input value={form.company} onChange={set('company')} autoComplete="organization" className={field} /></label>
      {contextOptions.length > 0 && (
        <label className={`${label} md:col-span-2`}>{contextLabel}
          <select value={form.context} onChange={set('context')} className={field}>
            <option value="">Select…</option>
            {contextOptions.map((b) => <option key={b}>{b}</option>)}
          </select>
        </label>
      )}
      <label className={`${label} md:col-span-2`}>Anything we should know?<textarea rows={4} value={form.message} onChange={set('message')} className={field} /></label>
      {state.error && <p role="alert" className="text-sm font-medium text-red-600 md:col-span-2">{state.error}</p>}
      <div className="md:col-span-2">
        {guard}
        <button type="submit" disabled={state.status === 'sending'} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#c8ff00] px-8 py-4 font-semibold text-[#0a1f2e] transition hover:brightness-95 active:scale-[0.98] disabled:opacity-60 md:w-auto">
          {state.status === 'sending' ? 'Sending…' : <>Get my quote <ArrowRight className="h-5 w-5" /></>}
        </button>
      </div>
    </form>
  );
};

/** Navy quote section: title, related links and the lead form (posts to the CRM leads API). */
export const QuoteSection = ({ title, sub, service, contextLabel, contextOptions, related = [] }) => (
  <section id="quote" className="scroll-mt-20 bg-[#0a1f2e] px-4 py-24 text-white sm:px-6 lg:px-8">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionTitle title={title} sub={sub} light />
        {related.length > 0 && (
          <Reveal className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-gray-400">Related</p>
            {related.map((r) => (
              <Link key={r.to} to={r.to} className="flex items-center justify-between rounded-2xl border border-white/10 px-5 py-4 font-semibold transition-colors hover:border-[#c8ff00] hover:text-[#c8ff00]">
                {r.label} <ArrowUpRight className="h-4 w-4" />
              </Link>
            ))}
          </Reveal>
        )}
      </div>
      <Reveal delay={120} className="lg:col-span-7">
        <QuoteForm service={service} contextLabel={contextLabel} contextOptions={contextOptions} />
      </Reveal>
    </div>
  </section>
);
